import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { normalizeAuthUser, type AuthUser, type JwtPayload } from './auth.types';
import { STORAGE_PORT } from './storage/storage.port';
import { isTokenExpired, tryDecodeJwtPayload } from './jwt';
import { AuthApi } from './auth.api';

export const ACCESS_TOKEN_KEY = 'access_token';
export const AUTH_USER_KEY = 'auth_user';

function decodePerms(token: string | null): string[] {
  const payload = tryDecodeJwtPayload<JwtPayload>(token);
  if (!payload) return [];
  if (typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now()) return [];
  const perms = payload.perms ?? payload.permisos;
  if (payload.permisos && !payload.perms) {
    console.warn('[AuthStore] JWT usa "permisos" deprecado, migrar a "perms"');
  }
  return Array.isArray(perms) ? perms : [];
}

/**
 * Signals-based store managing auth state.
 *
 * Store layer — signals-based state management.
 * No business logic, no HTTP calls.
 *
 * Follows the OnboardingStore pattern but simpler —
 * just user + token + loading/error states.
 */
@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly storage =
    inject(STORAGE_PORT, { optional: true }) ??
    ({
      getItem: (k: string) => localStorage.getItem(k),
      setItem: (k: string, v: string) => localStorage.setItem(k, v),
      removeItem: (k: string) => localStorage.removeItem(k),
    } as const);
  private readonly _user = signal<AuthUser | null>(null);
  private readonly _accessToken = signal<string | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly injector = inject(Injector);

  readonly user = this._user.asReadonly();
  readonly accessToken = this._accessToken.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly isAuthenticated = computed(() => {
    const token = this._accessToken();
    if (!token) return false;
    return !isTokenExpired(token);
  });

  readonly permissions = computed(() => decodePerms(this._accessToken()));

  // Phase 53 — onboarding gating (D-01..D-04)
  // Fast signal: aud=tenso-onboarding or !activeBusinessId or businessIds empty => needs onboarding
  // Backward compat: tokens without business fields (legacy tests) are considered NOT onboarding
  readonly needsOnboarding = computed(() => {
    const token = this._accessToken();
    if (!token) return false;
    if (isTokenExpired(token)) return false;
    const payload = tryDecodeJwtPayload<JwtPayload & { businessIds?: string[]; activeBusinessId?: string | null; aud?: string | string[] }>(token);
    if (!payload) return false;
    const rec = payload as unknown as Record<string, unknown>;
    const aud = rec['aud'] as string | string[] | undefined;
    if (Array.isArray(aud) ? aud.includes('tenso-onboarding') : aud === 'tenso-onboarding') return true;
    const hasBusinessIds = 'businessIds' in rec;
    const hasActive = 'activeBusinessId' in rec;
    // Legacy tokens without business fields → not onboarding (avoid breaking old tests)
    if (!hasBusinessIds && !hasActive) return false;
    const active = rec['activeBusinessId'] as string | null | undefined;
    const ids = rec['businessIds'] as string[] | undefined;
    if (!active) return true;
    if (!ids || ids.length === 0) return true;
    return false;
  });

  hasPermiso(required: string | string[]): boolean {
    const perms = this.permissions();
    if (Array.isArray(required)) return required.some((p) => perms.includes(p));
    return perms.includes(required);
  }

  hasRole(role: string): boolean {
    const payload = tryDecodeJwtPayload<JwtPayload>(this._accessToken());
    if (!payload) return false;
    return payload.rol === role;
  }

  constructor() {
    this.restoreFromStorage();
  }

  /** Called after successful login — stores token + user (normalizes legacy flat shape) */
  setSession(accessToken: string, user: AuthUser): void {
    const normalized = normalizeAuthUser(user);
    this._accessToken.set(accessToken);
    this._user.set(normalized);
    this._error.set(null);
    this.storage.setItem(ACCESS_TOKEN_KEY, accessToken);
    this.storage.setItem(AUTH_USER_KEY, JSON.stringify(normalized));
  }

  /** Called after successful refresh — updates only the token */
  updateToken(accessToken: string): void {
    this._accessToken.set(accessToken);
    this.storage.setItem(ACCESS_TOKEN_KEY, accessToken);
  }

  /** Called on login error — sets error message */
  setError(message: string): void {
    this._error.set(message);
  }

  /** Called on refresh failure or logout — clears everything */
  clearSession(): void {
    this._accessToken.set(null);
    this._user.set(null);
    this._error.set(null);
    this.storage.removeItem(ACCESS_TOKEN_KEY);
    this.storage.removeItem(AUTH_USER_KEY);
  }

  setLoading(loading: boolean): void {
    this._loading.set(loading);
  }

  // Phase 53 — hybrid confirmation: fast signal + GET /auth/me definitive (D-02..D-04)
  // Always-fetch to avoid stale JWT after setup in another tab; fallback to fast signal on 401/offline.
  async confirmNeedsOnboarding(): Promise<boolean> {
    if (!this.needsOnboarding()) return false;
    try {
      // Lazy get AuthApi via Injector to avoid circular DI with HttpClient interceptors (interceptor injects AuthStore)
      const authApi = this.injector.get(AuthApi);
      const me = await firstValueFrom(authApi.me());
      const hasBusiness = !!me.businessId && me.businessId !== '';
      if (!hasBusiness) return true;
      return false;
    } catch {
      const token = this._accessToken();
      if (!token || isTokenExpired(token)) {
        return false;
      }
      return this.needsOnboarding();
    }
  }

  private restoreFromStorage(): void {
    const token = this.storage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      this._accessToken.set(token);
    }
    const userJson = this.storage.getItem(AUTH_USER_KEY);
    if (userJson) {
      try {
        const raw = JSON.parse(userJson) as unknown;
        const user = normalizeAuthUser(raw);
        this._user.set(user);
        // Migrate old flat storage to new shape eagerly
        this.storage.setItem(AUTH_USER_KEY, JSON.stringify(user));
      } catch {
        this.storage.removeItem(AUTH_USER_KEY);
      }
    }
  }
}
